import enCore from "./locales/en/core.json";
import enContent from "./locales/en/content.json";
import enGenerator from "./locales/en/generator.json";
import enHelpShortcuts from "./locales/en/help/shortcuts.json";
import enHelpTips from "./locales/en/help/tips.json";
import enNavigation from "./locales/en/navigation.json";
import enMessages from "./locales/en/messages.json";
import heCore from "./locales/he/core.json";
import heContent from "./locales/he/content.json";
import heGenerator from "./locales/he/generator.json";
import heHelpShortcuts from "./locales/he/help/shortcuts.json";
import heHelpTips from "./locales/he/help/tips.json";
import heNavigation from "./locales/he/navigation.json";
import heMessages from "./locales/he/messages.json";
import {
  APP_LANGUAGE_ENGLISH,
  APP_LANGUAGE_HEBREW,
  APP_LANGUAGE_SYSTEM
} from "../lib/settings/appSettings.js";

const fallbackLanguage = APP_LANGUAGE_ENGLISH;
const rtlLanguages = new Set([APP_LANGUAGE_HEBREW]);
const namespaceSelfKey = "_self";

function isPlainLocaleObject(value) {
  return Boolean(value)
    && typeof value === "object"
    && !Array.isArray(value);
}

export function mergeLocaleParts(...parts) {
  return parts.reduce((merged, part) => {
    Object.entries(part ?? {}).forEach(([key, value]) => {
      const existingValue = merged[key];

      if (isPlainLocaleObject(value) && isPlainLocaleObject(existingValue)) {
        merged[key] = mergeLocaleParts(existingValue, value);
        return;
      }

      if (typeof existingValue === "string" && isPlainLocaleObject(value)) {
        merged[key] = mergeLocaleParts({ [namespaceSelfKey]: existingValue }, value);
        return;
      }

      if (isPlainLocaleObject(existingValue) && typeof value === "string") {
        merged[key] = mergeLocaleParts(existingValue, { [namespaceSelfKey]: value });
        return;
      }

      merged[key] = value;
    });

    return merged;
  }, {});
}

const en = {
  ...mergeLocaleParts(
    enCore,
    enContent,
    enGenerator,
    enHelpShortcuts,
    enHelpTips,
    enNavigation,
    enMessages
  )
};
const he = {
  ...mergeLocaleParts(
    heCore,
    heContent,
    heGenerator,
    heHelpShortcuts,
    heHelpTips,
    heNavigation,
    heMessages
  )
};
const dictionaries = {
  [APP_LANGUAGE_ENGLISH]: en,
  [APP_LANGUAGE_HEBREW]: he
};

function getNestedValue(dictionary, key) {
  return key.split(".").reduce((value, part) => value?.[part], dictionary);
}

function getTranslatableValue(value) {
  if (typeof value === "string") {
    return value;
  }

  if (isPlainLocaleObject(value) && typeof value[namespaceSelfKey] === "string") {
    return value[namespaceSelfKey];
  }

  return null;
}

function resolveDictionaries(language) {
  const activeLanguage = resolveAppLanguage(language);

  return {
    activeDictionary: dictionaries[activeLanguage] ?? dictionaries[fallbackLanguage],
    fallbackDictionary: dictionaries[fallbackLanguage]
  };
}

function normalizeBrowserLanguage(language) {
  return String(language || "").toLowerCase().split("-")[0];
}

export function resolveAppLanguage(language) {
  if (language && language !== APP_LANGUAGE_SYSTEM) {
    return dictionaries[language] ? language : fallbackLanguage;
  }

  if (typeof navigator !== "undefined") {
    const browserLanguage = normalizeBrowserLanguage(navigator.language);
    if (dictionaries[browserLanguage]) {
      return browserLanguage;
    }
  }

  return fallbackLanguage;
}

export function getDirectionForLanguage(language) {
  return rtlLanguages.has(language) ? "rtl" : "ltr";
}

export function getTranslationNode(language, key) {
  const { activeDictionary, fallbackDictionary } = resolveDictionaries(language);

  return getNestedValue(activeDictionary, key) ?? getNestedValue(fallbackDictionary, key) ?? null;
}

export function createTranslator(language) {
  const { activeDictionary, fallbackDictionary } = resolveDictionaries(language);

  return function translate(key, params = {}) {
    const value = getTranslatableValue(getNestedValue(activeDictionary, key))
      ?? getTranslatableValue(getNestedValue(fallbackDictionary, key));

    if (value === null) {
      return key;
    }

    return value.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, token) => {
      return Object.hasOwn(params, token) ? String(params[token]) : match;
    });
  };
}
